import React from "react";
import { Helmet } from "react-helmet";
import { Link } from "react-router-dom";

// 2026 High-end assets: Focus on grain detail and "Organic Modern" styling
const woodenImages = {
  hero: "https://images.unsplash.com/photo-1560448204-e02f11c3d0e2?auto=format&fit=crop&w=1200&q=80",
  detail: "https://images.unsplash.com/photo-1505691938895-1758d7eaa511?auto=format&fit=crop&w=600&q=80",
  homeOffice: "https://images.unsplash.com/photo-1593062096033-9a26b09da705?auto=format&fit=crop&w=600&q=80",
};

function WoodenBlinds() {
  return (
    <div className="max-w-7xl mx-auto px-6 py-12 text-stone-900 font-sans">
      <Helmet>
        <title>Wooden Blinds UK | Real Basswood & Faux Wood Collection</title>
        <meta
          name="description"
          content="Transform your home with our 2026 Wooden Blinds collection. Sustainable real wood and 100% waterproof faux wood options. Made to measure in the UK."
        />
        <meta name="keywords" content="Wooden Blinds UK, Real Wood Venetians, Faux Wood Blinds, Basswood Blinds, Sustainable Window Treatments, White Wooden Blinds with Tapes" />
      </Helmet>

      {/* --- Section 1: Hero - The Natural Aesthetic --- */}
      <header className="grid lg:grid-cols-2 gap-16 items-center mb-24">
        <div>
          <span className="text-orange-800 font-bold tracking-widest uppercase text-xs mb-3 block">Organic Modern Living</span>
          <h1 className="text-5xl md:text-7xl font-bold mb-6 leading-tight tracking-tight text-stone-900">
            Natural <br /><span className="text-orange-800">Wooden Blinds</span>
          </h1>
          <p className="text-xl text-stone-600 mb-8 leading-relaxed">
            Nothing compares to the organic warmth of wood. In 2026, the trend has shifted toward <strong>visible grains</strong> and <strong>matte finishes</strong>. Whether you choose sustainable Basswood or moisture-resistant Faux Wood, our blinds offer a timeless luxury that grounds your interior design.
          </p>
          <div className="flex flex-col sm:flex-row gap-4">
            <button className="bg-stone-900 text-white px-10 py-4 rounded-lg font-bold hover:bg-orange-900 transition-all shadow-xl">
              Shop Real Wood
            </button>
            <button className="bg-white border-2 border-stone-200 px-10 py-4 rounded-lg font-bold hover:bg-stone-50 transition">
              Order Free Swatches
            </button>
          </div>
        </div>
        <div className="relative">
          <img 
            src={woodenImages.hero} 
            alt="Oak wooden blinds in a sun-drenched living room" 
            className="rounded-3xl shadow-2xl h-[550px] w-full object-cover"
          />
          <div className="absolute -bottom-6 -left-6 bg-white p-6 rounded-2xl shadow-xl border border-stone-100 hidden md:block max-w-[200px]">
            <p className="text-xs font-bold text-stone-400 uppercase tracking-tighter mb-2">Eco-Choice</p>
            <p className="text-sm font-semibold text-stone-800 italic">"FSC-Certified sustainable Basswood as standard."</p>
          </div>
        </div>
      </header>

      {/* --- Section 2: Technical Comparison (Real vs Faux) --- */}
      <section className="mb-24 py-16 bg-stone-50 rounded-[3rem] px-8 md:px-16 border border-stone-100">
        <h2 className="text-3xl font-bold text-center mb-16">Real Wood vs. Faux Wood: Which is right for you?</h2>
        <div className="grid md:grid-cols-2 gap-12">
          <div className="bg-white p-8 rounded-2xl shadow-sm">
            <h3 className="text-2xl font-bold mb-4 flex items-center gap-2">🌲 Real Wood</h3>
            <p className="text-stone-600 mb-6">Crafted from premium, lightweight Basswood. Offers a unique grain texture and natural insulation.</p>
            <ul className="space-y-3 text-sm font-medium text-stone-700">
              <li className="flex items-center gap-2">✔️ Lightweight (Perfect for large windows)</li>
              <li className="flex items-center gap-2">✔️ Natural thermal properties</li>
              <li className="flex items-center gap-2">❌ Not suitable for high-moisture areas</li>
            </ul>
          </div>
          <div className="bg-white p-8 rounded-2xl shadow-sm">
            <h3 className="text-2xl font-bold mb-4 flex items-center gap-2">🌊 Faux Wood</h3>
            <p className="text-stone-600 mb-6">High-grade PVC that looks identical to wood but is 100% waterproof and wipe-clean.</p>
            <ul className="space-y-3 text-sm font-medium text-stone-700">
              <li className="flex items-center gap-2">✔️ 100% Waterproof (Bathrooms & Kitchens)</li>
              <li className="flex items-center gap-2">✔️ Extremely durable & heat resistant</li>
              <li className="flex items-center gap-2">❌ Heavier than real wood</li>
            </ul>
          </div>
        </div>
      </section>

      {/* --- Section 3: Decorative Tapes (The 2026 Style Up) --- */}
      <section className="grid lg:grid-cols-2 gap-16 items-center mb-24">
        <img src={woodenImages.detail} alt="Close up of wooden blinds with decorative cloth tapes" className="rounded-3xl shadow-lg" />
        <div>
          <h2 className="text-3xl font-bold mb-6">The "Designer" Finish: Decorative Tapes</h2>
          <p className="text-lg text-stone-700 mb-6">
            Elevate your wooden blinds by adding <strong>luxury cotton tapes</strong>. Beyond hiding the internal cords, tapes allow you to introduce a secondary color to your window, bridging the gap between your blinds and your soft furnishings.
          </p>
          <div className="grid grid-cols-2 gap-4">
            <div className="p-4 bg-stone-100 rounded-xl">
              <p className="font-bold text-stone-900">25mm Tapes</p>
              <p className="text-xs text-stone-500 italic">For a classic, refined look.</p>
            </div>
            <div className="p-4 bg-stone-100 rounded-xl">
              <p className="font-bold text-stone-900">38mm Tapes</p>
              <p className="text-xs text-stone-500 italic">For a bold, shutter-style statement.</p>
            </div>
          </div>
        </div>
      </section>

      {/* --- Section 4: SEO FAQ --- */}
      <section className="max-w-4xl mx-auto mb-24">
        <h2 className="text-3xl font-bold mb-10 text-center">Wooden Blinds Buying Guide</h2>
        <div className="space-y-6">
          <details className="group border-b border-stone-200 pb-6">
            <summary className="list-none font-bold text-xl cursor-pointer flex justify-between items-center text-stone-800">
              Will wooden blinds warp in the sun?
              <span className="group-open:rotate-180 transition-transform">▼</span>
            </summary>
            <p className="mt-4 text-stone-600 leading-relaxed">Our real wood blinds are kiln-dried to minimize warping, but for south-facing windows with intense direct heat, we recommend our <strong>Faux Wood range</strong>, which is specifically engineered to handle extreme temperature fluctuations without losing its shape.</p>
          </details>
          <details className="group border-b border-stone-200 pb-6">
            <summary className="list-none font-bold text-xl cursor-pointer flex justify-between items-center text-stone-800">
              How do I clean wooden blinds?
              <span className="group-open:rotate-180 transition-transform">▼</span>
            </summary>
            <p className="mt-4 text-stone-600 leading-relaxed">For real wood, a dry microfibre cloth or a light dusting is best. Avoid water as it can seep into the grain. For Faux wood, you can use a damp cloth and mild detergent for a deeper clean.</p>
          </details>
        </div>
      </section>

      {/* --- Section 5: Internal Link Navigation & Footer --- */}
      <footer className="bg-stone-900 text-white p-12 md:p-20 rounded-[3rem] text-center">
        <h2 className="text-4xl font-bold mb-6 italic">Define Your Style.</h2>
        <p className="text-stone-400 mb-10 max-w-xl mx-auto">Explore our 2026 collection featuring over 40 wood stains, from Arctic White to Deep Walnut.</p>
        
        <div className="flex flex-wrap justify-center gap-6 mb-12">
          <Link to="/kitchen-blinds" className="text-white hover:text-orange-400 font-medium">Kitchen Ready</Link>
          <Link to="/bathroom-blinds" className="text-white hover:text-orange-400 font-medium">Bathroom Ready</Link>
          <Link to="/living-room-blinds" className="text-white hover:text-orange-400 font-medium">Lounge Inspiration</Link>
        </div>

        <button className="bg-orange-800 text-white px-12 py-5 rounded-full text-xl font-bold shadow-2xl hover:bg-orange-700 transition-all hover:scale-105">
          Order Your Free Wood Samples
        </button>
      </footer>
    </div>
  );
}

export default WoodenBlinds;